import { useEffect } from "react";
import { useUi, type WorkspaceApp } from "@/store/ui";
import { useTabs } from "@/store/tabs";
import { useAllSpaceItems } from "@/data/store";
import { Rail } from "./Rail";
import { TreePanel } from "./TreePanel";
import { TasksSidebar } from "@/tasks/TasksSidebar";
import { TasksApp } from "@/tasks/TasksApp";
import { CardsView } from "@/cards/CardsView";
import { CardsSidebar } from "@/cards/CardsSidebar";
import { GraphView } from "@/graph/GraphView";
import { GraphSidebar } from "@/graph/GraphSidebar";
import { useGraphView } from "@/graph/useGraphData";
import { SignInBanner } from "@/components/SignInBanner";
import { CARDS_TAB } from "@/commands/cards";
import { TASKS_DASHBOARD_TAB } from "@/commands/tasks";
import { CenterArea } from "./CenterArea";
import { RightPanel } from "./RightPanel";
import { StatusBar } from "./StatusBar";
import { ResizeHandle } from "./ResizeHandle";
import { MobileShell } from "./MobileShell";
import { useMediaQuery, MOBILE_QUERY } from "./useMediaQuery";
import s from "./AppShell.module.css";

export function AppShell() {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  useLegacyProductTabCleanup();
  if (isMobile) return <MobileShell />;
  return <DesktopShell />;
}

/** Remove product tabs left behind by versions before the app switcher. */
function useLegacyProductTabCleanup() {
  const spaces = useAllSpaceItems();
  useEffect(() => {
    const taskListIds = spaces.flatMap((space) =>
      space.items.filter((item) => item.type === "tasklist").map((item) => item.id),
    );
    useTabs.getState().closeItemEverywhere([CARDS_TAB, TASKS_DASHBOARD_TAB, ...taskListIds]);
  }, [spaces]);
}

/** Keyed rather than chained ternaries: the old chain ended in an unguarded
 *  Cards fallback, so a new app silently rendered the Cards board. */
export const SIDEBAR_LABEL: Record<WorkspaceApp, string> = {
  notes: "Note tree",
  tasks: "Task lists",
  cards: "Card boards",
  graph: "Graph settings",
};

export function AppSidebar({ app }: { app: WorkspaceApp }) {
  switch (app) {
    case "notes":
      return <TreePanel />;
    case "tasks":
      return <TasksSidebar />;
    case "cards":
      return <CardsSidebar />;
    case "graph":
      return <GlobalGraphSidebar />;
  }
}

export function AppCenter({ app }: { app: WorkspaceApp }) {
  switch (app) {
    case "notes":
      return <CenterArea />;
    case "tasks":
      return <TasksApp />;
    case "cards":
      return <CardsView />;
    case "graph":
      return <GraphView />;
  }
}

/** The graph's sidebar needs the live stats, so it reads the view itself. */
function GlobalGraphSidebar() {
  const { data, full, warnings } = useGraphView("global");
  return <GraphSidebar scope="global" stats={data.stats} full={full} warnings={warnings} />;
}

function DesktopShell() {
  const ui = useUi();

  // Panel widths live in CSS variables so any style can align to them.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--tree-w", `${ui.treeWidth}px`);
    root.style.setProperty("--right-w", `${ui.rightWidth}px`);
  }, [ui.treeWidth, ui.rightWidth]);

  return (
    <div className={s.shell}>
      <div className={s.rail}>
        <Rail />
      </div>

      <aside
        className={[s.tree, ui.treeCollapsed ? s.treeCollapsed : ""].join(" ")}
        aria-label={SIDEBAR_LABEL[ui.activeApp]}
      >
        <AppSidebar app={ui.activeApp} />
        <ResizeHandle side="left" onResize={(d) => ui.setTreeWidth(ui.treeWidth + d)} onDoubleClick={ui.toggleTree} />
      </aside>

      <main className={s.center}>
        <div className={s.appColumn}>
          <SignInBanner />
          <AppCenter app={ui.activeApp} />
        </div>
      </main>

      {ui.activeApp === "notes" ? (
        <aside className={[s.right, ui.rightCollapsed ? s.rightCollapsed : ""].join(" ")} aria-label="Note details">
          <RightPanel />
          <ResizeHandle side="right" onResize={(d) => ui.setRightWidth(ui.rightWidth + d)} onDoubleClick={ui.toggleRight} />
        </aside>
      ) : null}

      <div className={s.status}>
        <StatusBar />
      </div>
    </div>
  );
}
